import { z } from 'zod';

const num = () => z.coerce.number();

const AgeBucketEnum = z.enum(['Never Went to Store', '<30d', '30-60d', '60-90d', '90-180d', '180d+']);
export type AgeBucket = z.infer<typeof AgeBucketEnum>;
/* Accept the legacy "Pre-store" label from older card responses and normalise to
   "Never Went to Store". Can be replaced with AgeBucketEnum directly once the
   card update has propagated and any cached payloads expire. */
const AgeBucketInput = z.preprocess(
  v => (v === 'Pre-store' ? 'Never Went to Store' : v),
  AgeBucketEnum,
);

/* Brand × age_bucket rollup — primary stock-ageing payload (card 6976).
   Server-side aggregation keeps the payload ~constant (~400 rows) regardless
   of catalog size. */
export const StockAgeingBrandBucketSchema = z.array(z.object({
  brand_name: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  brand_manager: z.string().nullable(),
  age_bucket: AgeBucketInput,
  styles_count: num(),
  units_stocked: num(),
  units_sold_4w: num(),
  writedown_count: num(),
  /* Count of styles in this bucket whose first GRN entry was in the last 14 days.
     Optional until the card change has rolled out to every cached payload. */
  new_styles_count: num().optional(),
  avg_age: num(),
}));
export type StockAgeingBrandBucket = z.infer<typeof StockAgeingBrandBucketSchema>;

/* Write-down candidates — lazy detail, capped at 200 rows server-side.
   Only the styles that meet age >= 90d AND units_sold_4w = 0 AND total_inv > 0. */
export const StockAgeingWritedownSchema = z.array(z.object({
  lk_style_number: z.string().nullable(),
  brand_name: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  brand_manager: z.string().nullable(),
  product_name: z.string().nullable(),
  image: z.string().nullable(),
  total_inv: num(),
  age_days: num(),
  age_bucket: AgeBucketInput,
}));
export type StockAgeingWritedown = z.infer<typeof StockAgeingWritedownSchema>;

/* Facility × age_bucket payload for the facility-distribution stacked-bar chart.
   Brand / portfolio / manager are included so the dashboard's existing filters
   can be applied client-side without re-querying. */
export const StockAgeingFacilityBucketSchema = z.array(z.object({
  brand_name: z.string().nullable(),
  merch_portfolio: z.string().nullable(),
  brand_manager: z.string().nullable(),
  facility_name: z.string(),
  facility_type: z.string(),
  age_bucket: AgeBucketInput,
  units_stocked: num(),
  styles_count: num(),
}));
export type StockAgeingFacilityBucket = z.infer<typeof StockAgeingFacilityBucketSchema>;
